import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { numify } from 'numify';
/////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { telegramWebApp } from '@/main';
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogOverlay,
} from '@/app/shared/components';
import { ActivitiesService } from '@/core/services';
import {
	setCanClaim,
	setCounter,
	setDiamonds,
	setPendingBuildAction,
	setRewardCollected,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import { ClaimDailyComboResponse } from '@/core/interfaces';
import { DialogTitle } from '@radix-ui/react-dialog';

interface ClaimDailyComboProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ClaimDailyCombo: React.FC<ClaimDailyComboProps> = ({
	isOpen,
	onClose,
}: ClaimDailyComboProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { levelCombo } = useCustomSelector((state) => state.counter);
	const { t } = useTranslation();

	/*********************
	// #region Functions
	**********************/
	const handleRequestDailyCombo = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');

		toast.promise(ActivitiesService.claimDailyCombo(), {
			loading: t('general.loading'),
			success: (data: ClaimDailyComboResponse) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'rewards',
					action: 'Claim daily combo',
				});

				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));
				// Set total coins
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));

				dispatch(setCanClaim(false));
				dispatch(setRewardCollected(true));
				dispatch(setPendingBuildAction(false));

				return t('dailycombo.successClaim');
			},
			error: t('dailycombo.errorClaim'),
		});
		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogOverlay className="bg-customer-950/70" />
			<DialogContent className="border-none bg-transparent">
				<div className="flex items-center space-x-2">
					<DialogTitle hidden></DialogTitle>
					<div className="grid flex-1 gap-1">
						<p className="text-xl text-center font-bold">
							{t('dailycombo.congratulations')}
						</p>

						<div className="flex items-center gap-3 justify-center my-2">
							<div className="flex items-center gap-1">
								<img
									className="w-[40px]"
									src={process.env.VITE_COIN_IMAGE}
									alt={process.env.VITE_COIN_NAME}
								/>
								<p className="font-bold text-2xl">
									{numify(levelCombo.coinsReward)}
								</p>
							</div>

							<div className="flex items-center gap-1">
								<img
									className="w-[40px]"
									src={process.env.VITE_MINI_DIAMOND_IMAGE}
									alt={process.env.VITE_COIN_NAME}
								/>
								<p className="font-bold text-2xl">
									{numify(levelCombo.diamondsReward)}
								</p>
							</div>
						</div>

						<img
							className="cursor-pointer mx-auto mb-6"
							src={process.env.VITE_DAILY_COMBO_COMPLETED_IMAGE}
						/>
					</div>
				</div>
				<DialogFooter className="sm:justify-start">
					<DialogClose asChild>
						<Button
							type="button"
							variant="primary"
							onClick={handleRequestDailyCombo}
							// className="py-6 w-full flex items-center gap-1 text-sm rounded-lg"
						>
							{t('general.claim')}
						</Button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
